import { Link } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { usePricingPagePackages } from '../../api/catalog';
import { toMajor } from '../../contracts/money';

/**
 * Live plans strip for the marketing pages — reads the real published platform
 * catalog (GET /pricing) so /home and /possignin always reflect the current
 * packages + prices, never a hardcoded list.
 */
export function PlansPreview() {
  const { data: plans = [] } = usePricingPagePackages();
  if (plans.length === 0) return null;

  const monthlies = plans.map((p) => p.priceMonthlyMinor).filter((m): m is number => typeof m === 'number' && m > 0);
  const cheapest = monthlies.length ? Math.min(...monthlies) : null;

  return (
    <section id="plans" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
      <div className="text-center max-w-2xl mx-auto">
        <p className="text-xs font-semibold uppercase tracking-wider text-brand">Plans</p>
        <h2 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
          Pricing that scales with your shop.
        </h2>
        <p className="mt-3 text-zinc-600 dark:text-zinc-300 leading-relaxed">
          {plans.length} plans in taka{cheapest != null ? <> — from <span className="font-semibold text-zinc-900 dark:text-zinc-100">৳{toMajor(cheapest).toLocaleString('en-US')}/mo</span></> : ''}. Every plan ships the full module suite; the tier sets your limits.
        </p>
      </div>

      <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {plans.map((p) => (
          <div key={p.id} className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50 p-4 text-center">
            <div className="text-sm font-semibold text-zinc-950 dark:text-zinc-50">{p.name}</div>
            <div className="mt-1.5 flex items-baseline justify-center gap-0.5">
              <span className="text-xl font-semibold tabular-nums text-zinc-950 dark:text-zinc-50">
                ৳{p.priceMonthlyMinor ? toMajor(p.priceMonthlyMinor).toLocaleString('en-US') : '—'}
              </span>
              <span className="text-xs text-zinc-500 dark:text-zinc-400">/mo</span>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 text-center">
        <Link
          to="/pricing"
          className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-sm font-medium border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition"
        >
          See full pricing <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </section>
  );
}
